"use client";

import { useMemo } from "react";

import { Icon } from "@/components/ui/icon";
import type { GitHubProfileSchema } from "@/lib/api/model";

import {
  COLLAPSED_REPO_COUNT,
  EXPANDED_REPO_COUNT,
} from "../../lib/constants";
import { formatLongDate } from "@/lib/date/format-long-date";

import { AccountHeader } from "./account-header";

export interface GithubBlockProps {
  profile: GitHubProfileSchema;
  expanded: boolean;
}

export function GithubBlock({ profile, expanded }: GithubBlockProps) {
  const { user, repos } = profile;
  const visibleRepos = useMemo(() => {
    const limit = expanded ? EXPANDED_REPO_COUNT : COLLAPSED_REPO_COUNT;
    return repos.slice(0, limit);
  }, [repos, expanded]);

  const registeredOn = formatLongDate(user.created_at);
  const publicRepos = user.public_repos ?? 0;
  const remainingRepoCount = Math.max(0, publicRepos - EXPANDED_REPO_COUNT);
  const moreReposHref = `${user.html_url}?tab=repositories`;

  return (
    <div className="flex w-full min-w-0 flex-col gap-4">
      <AccountHeader
        network="github.com"
        username={user.login}
        externalUrl={user.html_url}
        tooltip="Public profile data fetched live from GitHub."
        meta={
          <>
            {registeredOn && <span>Registered since {registeredOn}</span>}
            <span>{publicRepos.toLocaleString()} repositories</span>
          </>
        }
      />

      {visibleRepos.length > 0 && (
        <div className="flex w-full min-w-0 flex-col gap-2">
          <p className="text-sm font-medium leading-5 text-foreground">
            Repositories
          </p>
          <ul className="flex w-full min-w-0 flex-col gap-1.5 pl-1">
            {visibleRepos.map((repo) => (
              <li
                key={repo.id}
                className="flex min-w-0 items-start gap-2 text-[13px] leading-[18px] tracking-[-0.13px]"
              >
                <span
                  aria-hidden
                  className="mt-[7px] size-1 shrink-0 rounded-full bg-foreground/35"
                />
                <div className="flex min-w-0 flex-col">
                  <a
                    href={repo.html_url}
                    target="_blank"
                    rel="noreferrer"
                    className="truncate font-medium text-foreground transition-colors hover:underline"
                  >
                    {repo.full_name || `${user.login}/${repo.name}`}
                  </a>
                  {repo.language && (
                    <span className="truncate text-foreground/55">
                      {repo.language}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>

          {expanded && remainingRepoCount > 0 && (
            <a
              href={moreReposHref}
              target="_blank"
              rel="noreferrer"
              className="mt-1 inline-flex items-center gap-1 text-sm font-medium text-foreground transition-colors hover:underline"
            >
              View more {remainingRepoCount.toLocaleString()} repositories in
              GitHub
              <Icon name="IconArrowRight" size={14} />
            </a>
          )}
        </div>
      )}
    </div>
  );
}
